// src/components/MemberDetail.js
import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';

import { db, functions } from '../firebase';
import { doc, getDoc, Timestamp, collection, query, where, getDocs } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';

// Using React Bootstrap components
import { Container, Row, Col, Card, Spinner, Alert, Button, Breadcrumb, ListGroup, Badge } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPencilAlt, faTrashAlt, faUsers, faLayerGroup } from '@fortawesome/free-solid-svg-icons';
import { format } from 'date-fns';

import { useAuth } from '../context/AuthContext';
import MemberPhotoAvatar from './MemberPhotoAvatar';
import FormattedPhoneNumber from './FormattedPhoneNumber';

// Initialize Firebase Functions
const deleteMemberCallable = httpsCallable(functions, 'deleteMember');

// Helper: Format Birthday
const formatBirthday = (dateValue) => {
    if (dateValue instanceof Timestamp) {
        try {
            // UPDATED: Changed format to exclude the year
            return format(dateValue.toDate(), 'MMMM d');
        } catch (e) {
            console.error("Error formatting timestamp:", dateValue, e);
            return '';
        }
    }
    if (typeof dateValue === 'string') { return dateValue; }
    return '';
};

function MemberDetail() {
    const { familyId, memberId } = useParams();
    const navigate = useNavigate();
    const { currentUser, isAdmin, userFidClaim, loadingAuth, userOrgId } = useAuth();

    // --- State ---
    const [family, setFamily] = useState(null);
    const [member, setMember] = useState(null);
    const [roles, setRoles] = useState([]); // State for group roles
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // --- Combined useEffect for all data fetching ---
    useEffect(() => {
        if (loadingAuth) return;

        if (!familyId || !memberId || !userOrgId) {
            setError("Required IDs (Family, Member, or Org) are missing.");
            setLoading(false);
            return;
        }

        let isMounted = true;

        const fetchData = async () => {
            setLoading(true);
            setError(null);

            try {
                // --- Fetch in Parallel ---
                const familyDocRef = doc(db, 'family', familyId);
                const memberDocRef = doc(db, 'member', memberId);
                const rolesQuery = query(collection(db, 'role'), where('memberId', '==', memberId), where('orgId', '==', userOrgId));

                const [familyDocSnapshot, memberDocSnapshot, rolesSnapshot] = await Promise.all([
                    getDoc(familyDocRef),
                    getDoc(memberDocRef),
                    getDocs(rolesQuery)
                ]);

                if (!isMounted) return;

                // --- Process and Validate Data ---
                if (!familyDocSnapshot.exists()) throw new Error(`Associated family not found with ID: ${familyId}`);
                if (!memberDocSnapshot.exists()) throw new Error(`Member not found with ID: ${memberId}`);

                const familyData = { ...familyDocSnapshot.data(), id: familyDocSnapshot.id };
                const memberData = { ...memberDocSnapshot.data(), id: memberDocSnapshot.id };

                if (memberData.familyId !== familyId) {
                    throw new Error("Security check failed: This member does not belong to the specified family.");
                }

                const memberRoles = rolesSnapshot.docs.map(d => ({...d.data(), id: d.id}));
                memberRoles.sort((a,b) => (a.groupName || '').localeCompare(b.groupName || ''));

                // --- Set State ---
                setFamily(familyData);
                setMember(memberData);
                setRoles(memberRoles);

            } catch (err) {
                if (isMounted) {
                    console.error("Error fetching member details:", err);
                    setError(err.message || "An unexpected error occurred.");
                }
            } finally {
                if (isMounted) {
                    setLoading(false);
                }
            }
        };

        fetchData();

        return () => { isMounted = false; };
    }, [familyId, memberId, loadingAuth, userOrgId]);


    // Handler: Delete Member
    const handleDeleteMember = async (memberToDeleteId, memberToDeleteName) => {
        if (!window.confirm(`Are you sure you want to permanently delete ${memberToDeleteName || 'this member'}?`)) return;
        setError(null);
        try {
            const result = await deleteMemberCallable({ memberId: memberToDeleteId });
            if (result?.data?.success) {
                navigate(`/families/${familyId}`, { replace: true, state: { message: `${memberToDeleteName || 'Member'} deleted successfully.` } });
            } else {
                setError(result?.data?.message || "Server denied the member deletion request.");
            }
        } catch (err) {
             setError(`Failed to delete member: ${err.message || "Network/Function error"}`);
        }
    };


    // --- Loading/Error States ---
    if (loading) {
        return ( <Container fluid className="text-center mt-5"><Spinner animation="border" role="status"><span className="visually-hidden">Loading...</span></Spinner></Container> );
    }
    if (!currentUser) {
        return ( <Container fluid className="mt-3"><Alert variant="warning">Please <Alert.Link as={Link} to="/login">log in</Alert.Link> to view member details.</Alert></Container> );
    }
    if (error || !family || !member) {
        return ( <Container fluid className="mt-3"><Alert variant="danger">Error: {error || "Member or Family data could not be loaded."}</Alert></Container> );
    }

    // --- Determine Permissions ---
    const canModify = isAdmin || (userFidClaim && userFidClaim === family.id);

    // --- Render Page Content ---
    return (
        <Container fluid className="mt-3">
            {/* Breadcrumbs & Action Buttons */}
            <Row className="mb-3">
                <Col xs={12} md>
                    <Breadcrumb>
                        <Breadcrumb.Item linkAs={Link} linkProps={{ to: "/dashboard" }}>Dashboard</Breadcrumb.Item>
                        <Breadcrumb.Item linkAs={Link} linkProps={{ to: "/families" }}>Families</Breadcrumb.Item>
                        <Breadcrumb.Item linkAs={Link} linkProps={{ to: `/families/${family.id}` }}>{family.surname}</Breadcrumb.Item>
                        <Breadcrumb.Item active>{member.name}</Breadcrumb.Item>
                    </Breadcrumb>
                </Col>
                 <Col xs={12} md="auto">
                     {canModify && (
                         <div className="d-flex justify-content-md-end flex-wrap gap-2">
                            <Button variant="outline-secondary" size="sm" as={Link} to={`/families/${family.id}`} title={`Back to ${family.surname} Family`}> <FontAwesomeIcon icon={faUsers} className="me-1" /> Family View </Button>
                            <Button variant="warning" size="sm" as={Link} to={`/members/edit/${member.id}`} title={`Edit ${member.name}`}> <FontAwesomeIcon icon={faPencilAlt} className="me-1" /> Edit Member </Button>
                            <Button variant="danger" size="sm" title={`Delete ${member.name}`} onClick={() => handleDeleteMember(member.id, member.name)}> <FontAwesomeIcon icon={faTrashAlt} className="me-1" /> Delete Member </Button>
                         </div>
                     )}
                 </Col>
            </Row>

            {/* Display error messages from actions like delete */}
            {error && <Row><Col><Alert variant="danger" onClose={() => setError(null)} dismissible>{error}</Alert></Col></Row>}

            {/* Main Content Area */}
            <Row className="justify-content-center g-3">
                 <Col md={10} lg={8}>
                    {/* Member Info Card */}
                    <Card className="shadow-sm">
                        <Card.Header as="h2" className="text-center">{member.name}</Card.Header>
                        <Card.Body>
                            <Row>
                                <Col md={4} className="text-center mb-4 mb-md-0 d-flex flex-column align-items-center">
                                     <MemberPhotoAvatar
                                        src={member.photo}
                                        alt={member.name}
                                        size={200}
                                        cacheKey={member.imageUpdatedAt instanceof Timestamp ? member.imageUpdatedAt.toMillis().toString() : member.id}
                                    />
                                </Col>
                                <Col md={8}>
                                    <h4 className="border-bottom pb-2 mb-3">Member Information</h4>
                                    <dl className="row">
                                        <dt className="col-sm-3">Birthday</dt>
                                        <dd className="col-sm-9">{formatBirthday(member.birthday) || '-'}</dd>
                                        <dt className="col-sm-3">Cellphone</dt>
                                        <dd className="col-sm-9"><FormattedPhoneNumber phoneNumber={member.cellphone} fallback="-" /></dd>
                                        <dt className="col-sm-3">Email</dt>
                                        <dd className="col-sm-9" style={{ wordBreak: 'break-all' }}>{member.email || '-'}</dd>
                                        <dt className="col-sm-3">Family</dt>
                                        <dd className="col-sm-9"><Link to={`/families/${family.id}`}>{family.surname || 'View Family'}</Link></dd>
                                    </dl>
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>

                    {/* Group Memberships Card */}
                    {roles.length > 0 && (
                        <Card className="shadow-sm mt-3">
                            <Card.Header>
                                <FontAwesomeIcon icon={faLayerGroup} className="me-2" />
                                Group Memberships
                            </Card.Header>
                            <ListGroup variant="flush">
                                {roles.map(role => (
                                    <ListGroup.Item key={role.id} className="d-flex justify-content-between align-items-start">
                                        <div className="ms-2 me-auto">
                                            <div className="fw-bold">
                                                <Link to="/groups">{role.groupName || 'Unknown Group'}</Link>
                                            </div>
                                            {role.label && <span className="text-muted">{role.label}</span>}
                                        </div>
                                    </ListGroup.Item>
                                ))}
                            </ListGroup>
                        </Card>
                    )}
                 </Col>
            </Row>
        </Container>
    );
}

export default MemberDetail;
